import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { createPortal } from "react-dom";
import { cn } from "../../utils/classnames";
import { CloseIcon } from "../icons";
import { DURATION, EASE_OUT } from "./motionPresets";
import { useBodyScrollLock, useCloseOnEscape } from "./modalHooks";

/**
 * Animated modal shell: a soft tinted backdrop plus a panel that rises in.
 *
 * <MotionModal show={isOpen} onClose={close} labelledBy="my-title">
 *   …panel content…
 * </MotionModal>
 *
 * - The backdrop is a light charcoal tint with a small blur, so the page is
 *   dimmed rather than blacked out and the user keeps their place.
 * - Rendered in a portal on <body>, so no parent's overflow/z-index can clip it.
 * - Escape and a click on the backdrop close it; page scroll is locked while open.
 * - prefers-reduced-motion: fades only, no travel or scale.
 */
const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: DURATION.fast, ease: EASE_OUT } },
  exit: { opacity: 0, transition: { duration: 0.2, ease: EASE_OUT } },
};

const panelVariants = (shouldReduce) => ({
  hidden: shouldReduce ? { opacity: 0 } : { opacity: 0, y: 28, scale: 0.97 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: DURATION.base, ease: EASE_OUT, delay: 0.05 },
  },
  // Leaving is quicker than arriving so closing never feels sluggish.
  exit: shouldReduce
    ? { opacity: 0, transition: { duration: 0.15 } }
    : { opacity: 0, y: 16, scale: 0.98, transition: { duration: 0.2, ease: EASE_OUT } },
});

const MotionModal = ({
  show,
  onClose,
  labelledBy,
  closeOnBackdrop = true,
  // Separate from panelClassName because cn() only joins classes: two max-w
  // utilities would clash instead of the caller's one winning.
  maxWidthClassName = "tw:max-w-[800px]",
  panelClassName,
  children,
}) => {
  const shouldReduce = useReducedMotion();
  useBodyScrollLock(show);
  useCloseOnEscape(show, onClose);

  return createPortal(
    <AnimatePresence>
      {show && (
        // z-9999: above sticky headers, dropdowns and tooltips on every page.
        // Popups opened later are added to <body> later, so they stack on top.
        <div className="tw:fixed tw:inset-0 tw:z-[9999]">
          <motion.div
            className="tw:absolute tw:inset-0 tw:bg-[#1F1F1F]/80 tw:backdrop-blur-[3px]"
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={closeOnBackdrop ? onClose : undefined}
            aria-hidden="true"
          />

          {/* Outer box scrolls (tall content scrolls here, never the page);
              inner box centres the panel. They must be separate elements, or a
              panel taller than the screen gets its top cut off. */}
          <div className="tw:relative tw:h-full tw:overflow-y-auto">
            <div
              className="tw:flex tw:min-h-full tw:items-center tw:justify-center tw:p-3 tw:sm:p-6"
              onClick={(event) => {
                // Clicks on the empty area around the panel count as the backdrop.
                if (closeOnBackdrop && event.target === event.currentTarget) onClose?.();
              }}
            >
              <motion.div
                role="dialog"
                aria-modal="true"
                aria-labelledby={labelledBy}
                className={cn(
                  "tw:relative tw:w-full tw:bg-white tw:rounded-lg tw:shadow-[0_24px_64px_-16px_rgba(0,0,0,0.35)]",
                  maxWidthClassName,
                  panelClassName
                )}
                variants={panelVariants(shouldReduce)}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                {children}
              </motion.div>
            </div>
          </div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
};

/**
 * The round ✕ used by every popup. By default it floats in the panel's
 * top-right corner; `isInline` keeps it in the normal flow instead (for
 * popups whose content was laid out under a close-button row).
 */
export const ModalCloseButton = ({ onClick, className, isInline = false, label = "Close", iconColor = "#6B6B6B", ...buttonProps }) => (
  <motion.button
    type="button"
    aria-label={label}
    onClick={onClick}
    className={cn(
      "tw:z-10 tw:flex tw:h-9 tw:w-9 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:transition-colors tw:hover:bg-[#F6F6F6]",
      !isInline && "tw:absolute tw:right-4 tw:top-4",
      className
    )}
    whileTap={{ scale: 0.92 }}
    // e.g. id / data-bs-dismiss for the Bootstrap-driven login popups
    {...buttonProps}
  >
    <CloseIcon size={13} color={iconColor} />
  </motion.button>
);

export default MotionModal;
